import { getMalawiGrade } from "./gradingUtils";
import {
    canonicalReportPeriod,
    isClassTestExamLabel,
    isContinuousAssessmentPeriod,
    isExamResultVisibleOnPortal,
} from "./reportPeriodUtils";

function normalizeReportPeriod(value) {
    const period = canonicalReportPeriod(value);
    if (isClassTestExamLabel(period)) return "classTests";
    if (isContinuousAssessmentPeriod(period)) return "midTerm";
    if (period === "End of term" || !period) return "endOfTerm";
    return "endOfTerm";
}

function mapExamResultRow(row, educationLevel, levelCode, sclassName, portalOnly) {
    if (portalOnly && !isExamResultVisibleOnPortal(row)) return null;
    if (row?.archivedAt) return null;

    const marksObtained = Number(row.marksObtained) || 0;
    const gradeInfo = getMalawiGrade(marksObtained, educationLevel, levelCode, sclassName);
    const subjectName = row.subjectName || row.subName?.subName || row.subName || "—";
    const reportPeriod = row.reportPeriod || "End of term";

    return {
        subjectName,
        subjectCode: row.subjectCode || row.subName?.subCode || "",
        marksObtained,
        grade: row.grade || gradeInfo.grade,
        label: row.label || gradeInfo.label,
        passed: row.passed ?? gradeInfo.passed,
        reportPeriod,
        examLabel: isClassTestExamLabel(reportPeriod) ? reportPeriod : row.examLabel,
    };
}

function emptyPeriods() {
    return { classTests: [], midTerm: [], endOfTerm: [] };
}

function pushRow(resultsByPeriod, row) {
    const bucket = normalizeReportPeriod(row.reportPeriod);
    resultsByPeriod[bucket].push(row);
}

function filterActiveResultsByPeriod(resultsByPeriod) {
    if (!resultsByPeriod) return emptyPeriods();
    return {
        classTests: [...(resultsByPeriod.classTests || [])],
        midTerm: [...(resultsByPeriod.midTerm || [])],
        endOfTerm: [...(resultsByPeriod.endOfTerm || [])],
    };
}

/** Build period buckets from API payload or legacy student examResult rows. */
export function resolveStudentResultsByPeriod({
    reportCard,
    userDetails,
    portalOnly = true,
}) {
    if (reportCard?.resultsByPeriod) {
        const active = filterActiveResultsByPeriod(reportCard.resultsByPeriod);
        const hasAny = ["classTests", "midTerm", "endOfTerm"].some(
            (key) => (active[key] || []).length > 0,
        );
        if (hasAny) return active;
    }

    const educationLevel =
        reportCard?.educationLevel || userDetails?.educationLevel || "Secondary";
    const levelCode = reportCard?.levelCode || userDetails?.sclassName?.levelCode || "";
    const sclassName =
        reportCard?.sclassName ||
        userDetails?.sclassName?.sclassName ||
        (typeof userDetails?.sclassName === "string" ? userDetails.sclassName : "");

    const resultsByPeriod = emptyPeriods();

    const flatRows = (reportCard?.results?.length ? reportCard.results : null) ||
        (userDetails?.examResult || [])
            .filter((r) => r?.subName && r.marksObtained != null && !r?.archivedAt)
            .map((r) => ({
                ...r,
                subjectName: r.subName?.subName || r.subName,
                subjectCode: r.subName?.subCode || "",
            }));

    for (const raw of flatRows) {
        const row = mapExamResultRow(raw, educationLevel, levelCode, sclassName, portalOnly);
        if (row) pushRow(resultsByPeriod, row);
    }

    return resultsByPeriod;
}

export function averageFromResultRows(rows) {
    if (!rows?.length) return 0;
    const total = rows.reduce((sum, row) => sum + (Number(row.marksObtained) || 0), 0);
    return Number((total / rows.length).toFixed(2));
}

export function hasAnyPeriodResults(resultsByPeriod) {
    if (!resultsByPeriod) return false;
    return ["classTests", "midTerm", "endOfTerm"].some(
        (key) => (resultsByPeriod[key] || []).length > 0,
    );
}

export function formatArchivedTermLabel(term) {
    const parts = [term?.term, term?.academicYear].filter(Boolean);
    return parts.length ? parts.join(" · ") : "Past term";
}
